import React, { useEffect, useMemo, useRef, useState } from "react";
import strings from "translate/language";
import "./MobileOtpVerification.css";

const OTP_LENGTH = 4;
const COUNTRY_CODE = "+91";

const MobileOtpVerification = ({
  mobile,
  isMobileValid,
  mobileVerified,
  verifiedMobile = "",
  showSendOtp,
  otpSent,
  otp,
  sendingOtp = false,
  verifyingOtp = false,
  sendError = "",
  variant = "default",
  inlineSendOnly = false,
  suppressSendButton = false,
  onSendOtp,
  onVerifyOtp,
  onResendOtp,
  onOtpChange,
  onChangeMobile,
}) => {
  const otpInputRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const isGreVariant = variant === "gre";

  const otpDigits = useMemo(() => {
    const digits = String(otp || "")
      .replace(/\D/g, "")
      .split("");
    return Array.from({ length: OTP_LENGTH }, (_, index) => digits[index] || "");
  }, [otp]);

  const filledDigitCount = String(otp || "").replace(/\D/g, "").length;
  const activeCellIndex = Math.min(filledDigitCount, OTP_LENGTH - 1);
  const mobileDigits = String(mobile || "").replace(/\D/g, "");

  useEffect(() => {
    if (otpSent && !mobileVerified) {
      const timer = setTimeout(() => {
        otpInputRef.current?.focus();
      }, 120);
      return () => clearTimeout(timer);
    }
  }, [otpSent, mobileVerified]);

  const handleOtpInputChange = (event) => {
    onOtpChange(event.target.value.replace(/\D/g, "").slice(0, OTP_LENGTH));
  };

  const clearOtp = () => {
    onOtpChange("");
    otpInputRef.current?.focus();
  };

  const focusOtpInput = () => {
    otpInputRef.current?.focus();
  };

  const canSendOtp =
    showSendOtp !== undefined
      ? showSendOtp
      : isMobileValid && !mobileVerified && !otpSent && Boolean(mobile);

  const isSendOtpDisabled = inlineSendOnly
    ? !isMobileValid ||
      !String(mobile || "").trim() ||
      sendingOtp ||
      otpSent
    : !canSendOtp || !isMobileValid || !mobile || sendingOtp || otpSent;

  const isVerifyDisabled =
    verifyingOtp || String(otp || "").length !== OTP_LENGTH;

  const sendButtonClassName = [
    "visitor-otp__send-btn",
    isGreVariant ? "visitor-otp__send-btn--gre" : "",
    inlineSendOnly ? "visitor-otp__send-btn--inline" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const renderSendButton = () => (
    <button
      type="button"
      className={sendButtonClassName}
      onClick={onSendOtp}
      disabled={isSendOtpDisabled}
    >
      {sendingOtp ? (
        <span
          className="spinner-border spinner-border-sm mr-2"
          role="status"
          aria-hidden="true"
        />
      ) : (
        <i className="fa fa-paper-plane-o mr-2" aria-hidden="true" />
      )}
      {strings.sendOtpToVerify}
    </button>
  );

  const renderSendError = () =>
    sendError ? (
      <div className="visitor-otp__send-error" role="alert">
        <i
          className="fa fa-exclamation-circle visitor-otp__send-error-icon"
          aria-hidden="true"
        />
        <span>{sendError}</span>
      </div>
    ) : null;

  const isCurrentMobileVerified =
    mobileVerified &&
    Boolean(verifiedMobile) &&
    String(mobile || "").trim() === String(verifiedMobile).trim();

  if (isCurrentMobileVerified && !otpSent) {
    if (isGreVariant) {
      return null;
    }

    return (
      <div className={`visitor-otp${isGreVariant ? " visitor-otp--gre" : ""}`}>
        <div className="visitor-otp__verified" role="status">
          <i
            className="fa fa-check-circle visitor-otp__verified-icon"
            aria-hidden="true"
          />
          <span>{strings.mobileVerifiedSuccess}</span>
        </div>
      </div>
    );
  }

  if (inlineSendOnly) {
    return renderSendButton();
  }

  const showGreHelper =
    isGreVariant && !otpSent && mobileDigits.length > 0 && !isMobileValid;

  const stageClassName = [
    "visitor-otp",
    isGreVariant ? "visitor-otp--gre" : "",
    otpSent ? "visitor-otp--otp-open" : "",
    sendingOtp || verifyingOtp ? "visitor-otp--loading" : "",
    suppressSendButton ? "visitor-otp--send-inline" : "",
  ]
    .filter(Boolean)
    .join(" ");

  if (suppressSendButton && !otpSent && !showGreHelper) {
    if (!sendError) {
      return null;
    }
    return (
      <div className={stageClassName}>
        {renderSendError()}
      </div>
    );
  }

  return (
    <div className={stageClassName}>
      {!isGreVariant && !otpSent ? (
        <div className="visitor-otp__hint" role="status">
          <i
            className="fa fa-info-circle visitor-otp__hint-icon"
            aria-hidden="true"
          />
          <span>{strings.mobileNoWithoutCountryCodeHint}</span>
        </div>
      ) : null}

      {showGreHelper ? (
        <p className="visitor-otp__gre-helper" role="status">
          {strings.mobileOtpEnterValid}
        </p>
      ) : null}

      {canSendOtp && !suppressSendButton ? renderSendButton() : null}

      {otpSent ? (
        <div className="visitor-otp__card">
          <div className="visitor-otp__card-top">
            <div className="visitor-otp__card-icon" aria-hidden="true">
              <i className="fa fa-commenting-o" />
            </div>
            <p className="visitor-otp__card-title">{strings.otpCardSubtitle}</p>
            <div className="visitor-otp__mobile-chip">
              <span className="visitor-otp__card-mobile">
                {COUNTRY_CODE} {mobile}
              </span>
              {onChangeMobile ? (
                <button
                  type="button"
                  className="visitor-otp__change-mobile"
                  onClick={onChangeMobile}
                >
                  <i className="fa fa-pencil" aria-hidden="true" />
                  {strings.mobileOtpChangeNumber}
                </button>
              ) : null}
            </div>
          </div>

          <div className="visitor-otp__card-body">
            <p className="visitor-otp__entry-label">{strings.enterOtpSentTo}</p>

            <div
              className="visitor-otp__cells-wrap"
              onClick={focusOtpInput}
              role="presentation"
            >
              <div className="visitor-otp__cells">
                {otpDigits.map((digit, index) => (
                  <div
                    key={index}
                    className={[
                      "visitor-otp__cell",
                      digit ? "visitor-otp__cell--filled" : "",
                      isFocused && index === activeCellIndex
                        ? "visitor-otp__cell--active"
                        : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    {digit || (
                      <span className="visitor-otp__cell-placeholder">·</span>
                    )}
                  </div>
                ))}
              </div>
              <input
                ref={otpInputRef}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={otp}
                onChange={handleOtpInputChange}
                maxLength={OTP_LENGTH}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                aria-label={strings.enterOPT}
                className="visitor-otp__hidden-input"
              />
            </div>

            <button
              type="button"
              className={`visitor-otp__verify-btn${
                isGreVariant ? " visitor-otp__verify-btn--gre" : ""
              }`}
              onClick={onVerifyOtp}
              disabled={isVerifyDisabled}
            >
              {verifyingOtp ? (
                <span
                  className="spinner-border spinner-border-sm mr-2"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                <i className="fa fa-shield mr-2" aria-hidden="true" />
              )}
              {strings.verifyOtp}
            </button>

            <div className="visitor-otp__footer">
              <span className="visitor-otp__footer-hint">
                {strings.otpDidntReceive}
              </span>
              <div className="visitor-otp__actions">
                {String(otp || "").length > 0 ? (
                  <button
                    type="button"
                    className="visitor-otp__action-chip"
                    onClick={clearOtp}
                  >
                    {strings.clearOtp}
                  </button>
                ) : null}
                <button
                  type="button"
                  className="visitor-otp__action-chip visitor-otp__action-chip--primary"
                  onClick={onResendOtp}
                  disabled={sendingOtp}
                >
                  {sendingOtp ? (
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    />
                  ) : (
                    strings.resendOTP
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {renderSendError()}
    </div>
  );
};

export default MobileOtpVerification;
